/* 后端 API 客户端。
 *
 * 前后端分离后前端不假设与后端同源。地址按以下优先级解析：
 *   1. URL 参数 ?api=http://host:port   —— 临时切换，便于对不同后端调试
 *   2. localStorage 中保存的地址        —— 用户在系统页改过就记住
 *   3. 静态服务注入的 /__config.js      —— 部署时由运维指定
 *   4. 同源（Vite dev 有 /api 代理时走这条）
 *
 * 同一份构建产物可指向不同后端，换环境不用改代码。
 */

const STORAGE_KEY = 'nexus.apiBase'

function readSaved() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null // 隐私模式下 localStorage 会抛异常，忽略即可
  }
}

function writeSaved(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    /* 忽略 */
  }
}

function resolveBase() {
  const fromQuery = new URLSearchParams(location.search).get('api')
  if (fromQuery) {
    writeSaved(fromQuery)
    return fromQuery.replace(/\/+$/, '')
  }
  const saved = readSaved()
  if (saved) return saved.replace(/\/+$/, '')

  const injected = window.NEXUS_RUNTIME_CONFIG?.apiBase
  if (injected) return String(injected).replace(/\/+$/, '')

  // 留空 = 同源相对路径，配合 Vite 的 /api 代理
  return ''
}

let base = resolveBase()

async function request(path, options = {}) {
  const res = await fetch(base + path, { cache: 'no-store', ...options })
  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`)
    err.status = res.status
    throw err
  }
  return data
}

const post = (path, body) =>
  request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  })

export const api = {
  get base() {
    return base || location.origin
  },

  get rawBase() {
    return base
  },

  setBase(value) {
    base = String(value ?? '').trim().replace(/\/+$/, '')
    writeSaved(base)
    return base
  },

  /** 是否同源。跨源时后端必须放行本页来源，否则浏览器只会抛含糊的 Failed to fetch。 */
  get isSameOrigin() {
    if (!base) return true
    try {
      return new URL(base, location.href).origin === location.origin
    } catch {
      return false
    }
  },

  health: () => request('/api/health'),
  /** fresh=true 让后端作废缓存直接打交易所，用于人工"重新检查" */
  snapshot: (fresh = false) => request('/api/snapshot' + (fresh ? '?fresh=1' : '')),

  declareOpening: (qty, price, fee) =>
    post('/api/opening', { qty: String(qty), price: String(price), fee: String(fee) }),

  // --- 盘前计划：生成由后端每日自动完成，这里只有人工确认的几个动作 ---
  planApprove: (operator) => post('/api/plan/approve', { operator }),
  planRevoke: (reason) => post('/api/plan/revoke', { reason }),
  planCheck: (index, passed) => post('/api/plan/check', { index, pass: passed }),
  planLimits: (limits) => post('/api/plan/limits', { limits }),
  planRegenerate: () => post('/api/plan/regenerate', {}),
  gateAck: (gate, scope, reason) =>
    post('/api/gate/ack', { gate, scope, reason }),
  ratchetReopen: (reason) => post('/api/ratchet/reopen', { reason }),

  /* 受控平仓：预览只读，执行只发限价卖单。
     后端会按盘口把数量和价格再校验一遍，这里传的只是意向。 */
  closeoutPreview: (qty, bps) => {
    const p = new URLSearchParams()
    if (qty) p.set('qty', String(qty))
    if (bps) p.set('bps', String(bps))
    const q = p.toString()
    return request('/api/closeout/preview' + (q ? '?' + q : ''))
  },
  closeoutSlice: (qty, limitPrice, bps, hardFloor) =>
    post('/api/closeout/slice', {
      qty: qty ?? null,
      limit_price: limitPrice ?? null,
      max_slippage_bps: bps ?? null,
      // 绝对底价：连续平仓时防止一轮轮把价格往下砸
      hard_floor: hardFloor ?? null,
    }),

  /** 主交易账户之外的账户。慢接口（1+N 个签名请求），不要放进轮询 */
  accounts: () => request('/api/accounts'),

  // 风控状态：在岗 / 锁定 / 审计。放后端才能扛住刷新与换浏览器。
  riskStart: () => post('/api/risk/start', {}),
  riskPause: (reason) => post('/api/risk/pause', { reason }),
  riskProtect: (reason) => post('/api/risk/protect', { reason }),
  riskRelease: (reason) => post('/api/risk/release', { reason }),
  riskTransition: (state, reason) => post('/api/risk/transition', { state, reason }),
  riskLog: (action, reason) => post('/api/risk/log', { action, reason }),

  exchanges: () => request('/api/exchanges'),

  /** force 会跳过"活动挂单"检查；缺凭证一类的硬条件仍然拦得住 */
  switchExchange: (exchange, symbol = null, force = false) =>
    post('/api/exchange', { exchange, symbol, force }),

  cancelAll: () => request('/api/cancel-all', { method: 'POST' }),
}
